import { useEffect, useState } from "react"
import { Users, Radio, X, Search, UserPlus } from "lucide-react"
import { useTranslation } from "@/clientWeb/i18n/LanguageContext"
import { isEmailAddress } from "@/features/meetings/services/invitee-add"
import { DirectoryScopeTabs } from "./directory-scope-tabs"
import { LoadMoreSentinel } from "./load-more-sentinel"
import { AvatarPhoto } from "./avatar-photo"

interface Contact {
  id: string
  name: string
  imageUrl?: string
  subtitle?: string
  contactOrgName?: string
}

/** Discovery reach for the picker — all three browse with partial match:
 *  `subId` (your sub-org) · `scopeId` (your scope) · `global` (whole platform). */
export type DirectoryScope = 'subId' | 'scopeId' | 'global'

interface NewChatPageProps {
  contacts: Contact[]
  title?: string
  showActions?: boolean
  onClose?: () => void
  onSelectContact?: (contact: Contact) => void
  onCreateGroup?: () => void
  onCreateBroadcast?: () => void
  // ── Optional directory-scope mode (the PresencePage picker). When
  // `scope`/`onScopeChange` are provided, the component renders scope tabs and
  // treats `contacts` as ALREADY scoped/fetched by the parent (no local filter),
  // and forwards the live query via `onQueryChange`. Omit them and the component
  // behaves exactly as before (local filter over a static `contacts` list). ──
  scope?: DirectoryScope
  // The scope `contacts` actually came from, once the caller has widened past
  // the selected tab (e.g. tab is sub-Organization but nothing matched there,
  // so it shows Organization results instead). Omit or match `scope` to skip
  // the note entirely.
  resultScope?: DirectoryScope | null
  onScopeChange?: (scope: DirectoryScope) => void
  onQueryChange?: (query: string) => void
  loading?: boolean
  // Infinite scroll, scope mode only: another page exists for `resultScope`,
  // a page is currently being fetched, and the callback to fetch it.
  hasMore?: boolean
  loadingMore?: boolean
  onLoadMore?: () => void
}

const scopeLabel: Record<DirectoryScope, string> = {
  subId: 'sub-Organization',
  scopeId: 'Organization',
  global: 'Global',
}

export function NewChatPage({
  contacts,
  title,
  showActions = true,
  onClose,
  onSelectContact,
  onCreateGroup,
  onCreateBroadcast,
  scope,
  resultScope,
  onScopeChange,
  onQueryChange,
  loading = false,
  hasMore = false,
  loadingMore = false,
  onLoadMore,
}: NewChatPageProps) {
  const t = useTranslation()
  const headerTitle = title ?? t('presence.newChat')
  const [searchQuery, setSearchQuery] = useState("")

  // Scope mode: the parent owns fetching/scoping, so forward the query and skip
  // the local filter. Legacy mode: filter the static list locally as before.
  const scopeMode = scope !== undefined && onScopeChange !== undefined
  useEffect(() => {
    if (scopeMode) onQueryChange?.(searchQuery)
  }, [searchQuery, scopeMode, onQueryChange])

  const q = searchQuery.trim().toLowerCase()
  const filteredContacts = scopeMode
    ? contacts
    : contacts
        .filter((contact) => {
          if (!q) return true
          // Match against both display name AND subtitle (email when populated by
          // the chat picker), so users can paste an address from elsewhere.
          return (
            contact.name.toLowerCase().includes(q) ||
            (contact.subtitle ?? "").toLowerCase().includes(q)
          )
        })
        .sort((a, b) => a.name.localeCompare(b.name))

  return (
    <div className="flex flex-col h-full bg-card overflow-hidden">
      {/* Header */}
      <div className="flex items-center gap-2 px-4 py-3 border-b border-stroke-100 dark:border-border shrink-0">
        {onClose && (
          <button
            onClick={onClose}
            className="p-2 rounded-full hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        )}
        <h2 className="text-lg font-semibold text-foreground">{headerTitle}</h2>
        <input
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          placeholder={t('presence.searchContacts')}
          className="flex-1 bg-transparent text-base text-foreground placeholder:text-muted-foreground outline-none"
          autoFocus
        />
        {searchQuery && (
          <button
            onClick={() => setSearchQuery("")}
            className="p-2 rounded-full hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        )}
        {!searchQuery && (
          <button className="p-2 rounded-full hover:bg-muted text-muted-foreground hover:text-foreground transition-colors">
            <Search className="w-5 h-5" />
          </button>
        )}
      </div>

      {/* Scope tabs — only in directory-scope mode. All three browse with partial
          match: Global (whole platform), Organization (your scope), sub-Organization
          (your sub-org). */}
      {scopeMode && <DirectoryScopeTabs value={scope!} onChange={(s) => onScopeChange?.(s)} />}

      {/* Body */}
      <div className="flex-1 overflow-y-auto">
        {showActions && (
          <>
            <div className="pt-4">
              <div className="bg-card overflow-hidden">
                <button
                  onClick={onCreateGroup}
                  className="w-full flex items-center gap-4 px-4 py-3 hover:bg-muted/50 transition-colors"
                >
                  <div className="w-9 h-9 rounded-xl bg-primary flex items-center justify-center">
                    <Users className="w-5 h-5 text-white" />
                  </div>
                  <span className="text-base font-medium text-foreground">{t('presence.newGroup')}</span>
                </button>
                <button
                  onClick={onCreateBroadcast}
                  className="w-full flex items-center gap-4 px-4 py-3 hover:bg-muted/50 transition-colors"
                >
                  <div className="w-9 h-9 rounded-xl bg-primary flex items-center justify-center">
                    <Radio className="w-5 h-5 text-white" />
                  </div>
                  <span className="text-base font-medium text-foreground">{t('presence.newBroadcast')}</span>
                </button>
              </div>
            </div>
            {/* <div className="h-px bg-border mx-4" />
            <div className="h-4" /> */}
          </>
        )}

        <div className="px-4">
          {/* Results widened past the selected tab (e.g. sub-Organization had
              nothing, so this is actually Organization). Says so instead of
              leaving the reader to wonder why a "sub-Organization" tab is
              showing someone from elsewhere. */}
          {!loading && scopeMode && resultScope && resultScope !== scope && filteredContacts.length > 0 && (
            <p className="pt-3 pb-1 text-xs text-muted-foreground">
              {t('presence.widerScopeNote', { scope: scopeLabel[resultScope] })}
            </p>
          )}
          {loading ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <p className="text-sm text-muted-foreground">{t('presence.loading')}</p>
            </div>
          ) : filteredContacts.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <UserPlus className="w-12 h-12 text-muted-foreground/30 mb-4" />
              <p className="text-sm text-muted-foreground">
                {/* A search for a full email address widens all the way to
                    Global before giving up, so an empty result here already
                    means no scope had that address. A partial name search
                    (or an empty browse) gets the plain "no contacts" message
                    instead: that case was never about one missing address. */}
                {isEmailAddress(searchQuery) ? t('presence.globalNoMatch') : t('presence.noContacts')}
              </p>
            </div>
          ) : (
            filteredContacts.map(contact => (
              <div
                key={contact.id}
                onClick={() => onSelectContact?.(contact)}
                className="flex items-center gap-3 py-2 hover:bg-muted/50 cursor-pointer transition-colors"
              >
                <div className="w-12 h-12 rounded-full bg-muted overflow-hidden shrink-0">
                  <AvatarPhoto
                    src={contact.imageUrl}
                    alt={contact.name}
                    className="w-full h-full object-cover"
                    fallback={
                      <div className="w-full h-full flex items-center justify-center text-sm font-semibold text-muted-foreground bg-primary/10 text-primary">
                        {contact.name[0]?.toUpperCase() || "?"}
                      </div>
                    }
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-base text-sm font-semibold text-foreground truncate">{contact.name}</p>
                  <p className="text-xs text-muted-foreground truncate">
                    {contact.subtitle || t('presence.available')}
                  </p>
                  {contact.contactOrgName && contact.contactOrgName !== contact.subtitle && (
                    <p className="text-xs text-muted-foreground truncate">{contact.contactOrgName}</p>
                  )}
                </div>
              </div>
            ))
          )}
          {/* Infinite scroll, scope mode only: legacy mode's `contacts` is a
              static list with nothing more to fetch. */}
          {scopeMode && !loading && (
            <LoadMoreSentinel hasMore={hasMore} loading={loadingMore} onVisible={() => onLoadMore?.()} />
          )}
        </div>
      </div>
    </div>
  )
}
